import { AccountsReceivableReportSource, BidPaymentStatus } from "api";
import { buildQuery } from "helpers/reqparams";
import { ITEM_ROOT } from "odata-query";

/** Confirmed leaf DTO shared by the two settlement reports. */
export interface SettlementRow {
    bidId: number;
    isDistributionBid: boolean;
    counterpartyId?: number | null;
    counterpartyName?: string;
    legalPersonId?: number | null;
    legalPersonName?: string;
    carId?: number | null;
    carNumber?: string;
    route?: string;
    ndsTypeId?: number | null;
    nds?: string | null;
    price: number;
    remainingPayment?: number | null;
    remainingAmount: number;
    bidPaymentStatus: BidPaymentStatus;
    isPaymentOverdue: boolean;
    overdueDays: number;
    invoiceDate?: string | null;
    planPaymentDate?: string | null;
    factPaymentDate?: string | null;
    previousDayPayment?: number | null;
    comment?: string | null;
    isChecked: boolean;
}

export interface SettlementGroup {
    bids: SettlementRow[];
    date?: string | null;
    counterpartyId?: number | null;
    counterpartyName?: string;
}

export type SettlementGrouping = "dates" | "counterparties" | undefined;
export type SettlementFilter = { id: string; value: unknown };
export interface SettlementFilterContext {
    source: AccountsReceivableReportSource;
    isAccountsPayable: boolean;
    demoData?: boolean;
}

export type SettlementTableState = {
    filters?: SettlementFilter[];
    pageIndex?: number;
    pageSize?: number;
    sortBy?: { id: string; desc?: boolean }[];
    [key: string]: unknown;
};

/** Shared column controls store their payload inside react-table's filter.value. */
export function settlementFilterValue(filters: SettlementFilter[], id: string): unknown {
    const payload = filters.find((filter) => filter.id === id)?.value;
    return payload && typeof payload === "object" ? (payload as { value?: unknown }).value : undefined;
}

/** Normalize catalog IDs so equivalent VAT selections share one persisted/upcoming predicate. */
export function settlementNdsTypeIds(value: unknown): number[] {
    const selected = Array.isArray(value) ? value : value == null ? [] : [value];
    return [
        ...new Set(selected.filter((id): id is number => typeof id === "number" && Number.isFinite(id) && id > 0)),
    ].sort((left, right) => left - right);
}

/** Match the catalog identifier, keeping unset rates only when no rate filter is selected. */
export function settlementNdsTypeMatches(ndsTypeId: SettlementRow["ndsTypeId"], selected: readonly number[]): boolean {
    return selected.length === 0 || (ndsTypeId != null && selected.includes(ndsTypeId));
}

function carFilterMode(context: SettlementFilterContext) {
    return context.isAccountsPayable || context.source === AccountsReceivableReportSource.HiredCar
        ? "hired"
        : context.source === AccountsReceivableReportSource.All
          ? "text"
          : "own";
}

/** Build every supported server filter from the current controls, never from old extraParams. */
export function settlementServerFilters(filters: SettlementFilter[], context: SettlementFilterContext) {
    const idValue = (id: string) => {
        const value = settlementFilterValue(filters, id);
        return typeof value === "number" && Number.isFinite(value) && value > 0 ? value : undefined;
    };
    const selected = settlementFilterValue(filters, "bidPaymentStatus");
    const statuses = (Array.isArray(selected) ? selected : selected ? [selected] : [])
        .filter((value): value is BidPaymentStatus => Object.values(BidPaymentStatus).includes(value))
        .sort();
    return {
        counterpartyId: idValue("counterpartyName"),
        carId: carFilterMode(context) === "text" ? undefined : idValue("carNumber"),
        paymentStatus: statuses.length ? statuses : undefined,
    };
}

/** Live server predicates are authoritative; unsupported predicates and every demo filter stay local. */
export function settlementLocalFilters(filters: SettlementFilter[], context: SettlementFilterContext) {
    const serverIds = [
        "counterpartyName",
        "bidPaymentStatus",
        ...(carFilterMode(context) === "text" ? [] : ["carNumber"]),
    ];
    return context.demoData ? filters : filters.filter((filter) => !serverIds.includes(filter.id));
}

/** Own-fleet IDs, hired-car IDs and All's number search cannot share the same persisted car control. */
export function normalizeSettlementTableState(
    table: SettlementTableState | undefined,
    context: SettlementFilterContext,
) {
    const filters = table?.filters ?? [];
    const car = settlementFilterValue(filters, "carNumber");
    const valid =
        car == null ||
        car === "" ||
        (carFilterMode(context) === "text" ? typeof car === "string" : typeof car === "number");
    return { ...table, filters: valid ? filters : filters.filter((filter) => filter.id !== "carNumber") };
}

/** Clear only incompatible car state when the source or debt side changes its identifier namespace. */
export function settlementModeChange(
    state: SettlementFilterContext & {
        settlementTableState?: SettlementTableState;
    },
    name: "source" | "isAccountsPayable",
    value: AccountsReceivableReportSource | boolean,
) {
    const next = {
        source: name === "source" ? (value as AccountsReceivableReportSource) : state.source,
        isAccountsPayable: name === "isAccountsPayable" ? (value as boolean) : state.isAccountsPayable,
    };
    if (carFilterMode(state) === carFilterMode(next)) {
        return next;
    }
    return {
        ...next,
        settlementTableState: {
            ...state.settlementTableState,
            filters: (state.settlementTableState?.filters ?? []).filter((filter) => filter.id !== "carNumber"),
            pageIndex: 0,
        },
    };
}

/** Use the same literal number search in the All table and upcoming payments. */
export function settlementCarNumberMatches(number: string | undefined, value: unknown) {
    return !value || (typeof number === "string" && number.toLowerCase().includes(String(value).toLowerCase()));
}

export interface SettlementParams {
    isAccountsPayable?: boolean;
    startDate?: string | Date;
    endDate?: string | Date;
    counterpartyId?: number;
    carId?: number;
    paymentStatus?: string | string[];
    groupByDates?: boolean;
    groupByCounterparties?: boolean;
    onlyOverdue?: boolean;
    previousDayPaymentsOnly?: boolean;
    byPlanPaymentDate?: boolean;
    source?: AccountsReceivableReportSource;
}

interface SettlementUpcomingProps {
    source: AccountsReceivableReportSource;
    isAccountsPayable: boolean;
    demoData: boolean;
    revision: number;
    organizationTimezone: { utcOffset: string };
    filters: SettlementFilter[];
}

/** Effective upcoming dependencies exclude the main report's period, statuses and grouping. */
export function settlementUpcomingInputs(props: SettlementUpcomingProps) {
    const filterValue = (name: string) => settlementFilterValue(props.filters, name);
    const serverFilters = settlementServerFilters(props.filters, props);
    const allSources = !props.isAccountsPayable && props.source === AccountsReceivableReportSource.All;
    const car = filterValue("carNumber");
    return {
        isAccountsPayable: props.isAccountsPayable,
        source: props.isAccountsPayable ? undefined : props.source,
        counterpartyId: serverFilters.counterpartyId,
        carId: serverFilters.carId,
        legalPersonId: filterValue("legalPersonName"),
        ndsTypeIds: settlementNdsTypeIds(filterValue("nds")),
        carFilter: allSources && typeof car === "string" && car ? car.toLowerCase() : undefined,
        utcOffset: props.organizationTimezone.utcOffset,
        demoData: props.demoData,
        revision: props.revision,
    };
}

/** Mutations retain an explicit revision trigger even when the upcoming filters are unchanged. */
export function settlementUpcomingChanged(previous: SettlementUpcomingProps, current: SettlementUpcomingProps) {
    return JSON.stringify(settlementUpcomingInputs(previous)) !== JSON.stringify(settlementUpcomingInputs(current));
}

/** Aggregate only the server-provided residual and overdue flag; match the backend footer's paid formula. */
export function settlementReceived(
    row: Pick<SettlementRow, "price" | "remainingPayment" | "bidPaymentStatus">,
): number {
    return [BidPaymentStatus.Paid, BidPaymentStatus.PartiallyPaid].includes(row.bidPaymentStatus)
        ? row.price - (row.remainingPayment ?? 0)
        : 0;
}

/** Match report DTO/footers for history whose ReceivedAmount getter currently returns zero for Paid. */
export function settlementHistoryAmounts(history: {
    price?: number | null;
    remainingPayment?: number | null;
    paymentStatus: BidPaymentStatus;
}) {
    return {
        received:
            history.price == null
                ? null
                : settlementReceived({
                      price: history.price,
                      remainingPayment: history.remainingPayment,
                      bidPaymentStatus: history.paymentStatus,
                  }),
        remaining:
            history.paymentStatus === BidPaymentStatus.Paid ? 0 : (history.remainingPayment ?? history.price ?? null),
    };
}

/** A display-only progress percentage based on normalized money, never the history DTO's paidPercent. */
export function settlementPaidPercent(price?: number | null, received?: number | null): number | null {
    if (
        typeof price !== "number" ||
        !Number.isFinite(price) ||
        price <= 0 ||
        typeof received !== "number" ||
        !Number.isFinite(received)
    ) {
        return null;
    }
    return Math.round(Math.min(100, Math.max(0, (received / price) * 100)));
}

/** A type and ID pair prevents own-fleet bids and distribution orders from colliding. */
export function settlementKey(row: Pick<SettlementRow, "isDistributionBid" | "bidId">): string {
    return `${row.isDistributionBid ? "order" : "bid"}:${row.bidId}`;
}

/** Discard group headers and retain one copy of each underlying basis. */
export function settlementLeaves(data: (SettlementRow | SettlementGroup)[]): SettlementRow[] {
    const rows = data.flatMap((row) => ("bids" in row ? row.bids : [row]));
    return [...new Map(rows.map((row) => [settlementKey(row), row])).values()];
}

/** Compute report and group totals over the entire filtered collection, before pagination. */
export function settlementTotals(rows: SettlementRow[]) {
    const leaves = settlementLeaves(rows);
    const unpaid = leaves.filter((row) => row.bidPaymentStatus !== BidPaymentStatus.Paid);
    const previous = leaves.filter((row) => row.previousDayPayment != null);
    const sum = (items: SettlementRow[], field: (row: SettlementRow) => number) =>
        items.reduce((total, row) => total + field(row), 0);
    return {
        totalBidsCount: leaves.length,
        plannedPayments: sum(leaves, (row) => row.price),
        pendingPayments: sum(unpaid, (row) => row.remainingAmount),
        receivedAmount: sum(leaves, settlementReceived),
        paidAmount: sum(leaves, settlementReceived),
        pendingInvoicedPayments: sum(
            unpaid.filter((row) => !!row.invoiceDate),
            (row) => row.remainingAmount,
        ),
        totalDebt: sum(
            unpaid.filter((row) => row.isPaymentOverdue),
            (row) => row.remainingAmount,
        ),
        overdueDebt: sum(
            unpaid.filter((row) => row.isPaymentOverdue),
            (row) => row.remainingAmount,
        ),
        previousDayPayments: {
            totalAmount: sum(previous, (row) => row.previousDayPayment ?? 0),
            counterpartiesCount: new Set(previous.map((row) => row.counterpartyId).filter((id) => id != null)).size,
            bidsCount: previous.length,
        },
    };
}

export const settlementAggregateColumns = [
    "bidId",
    "price",
    "received",
    "remainingAmount",
    "overdueAmount",
    "previousDayPayment",
];

/** Apply leaf filters first, then filters on recomputed groups. Empty groups naturally disappear. */
export function filterSettlementRows(
    data: SettlementRow[],
    filters: SettlementFilter[],
    grouping: SettlementGrouping,
    applyFilters: (rows: Record<string, unknown>[], filters: SettlementFilter[]) => Record<string, unknown>[],
): SettlementRow[] {
    const leafFilters = filters.filter((filter) => !grouping || !settlementAggregateColumns.includes(filter.id));
    const leaves = applyFilters(
        data as unknown as Record<string, unknown>[],
        leafFilters,
    ) as unknown as SettlementRow[];
    if (!grouping) {
        return leaves;
    }
    const aggregateFilters = filters.filter((filter) => settlementAggregateColumns.includes(filter.id));
    const groups = new Map<string, SettlementRow[]>();
    for (const row of leaves) {
        const key = String(grouping === "dates" ? (row.planPaymentDate ?? "") : (row.counterpartyId ?? ""));
        groups.set(key, [...(groups.get(key) ?? []), row]);
    }
    const aggregates = [...groups.values()].map((rows) => {
        const totals = settlementTotals(rows);
        return {
            bidId: totals.totalBidsCount,
            price: totals.plannedPayments,
            received: totals.receivedAmount,
            remainingAmount: totals.pendingPayments,
            overdueAmount: totals.totalDebt,
            previousDayPayment: totals.previousDayPayments.totalAmount,
            rows,
        };
    });
    return applyFilters(aggregates, aggregateFilters).flatMap((group) => group.rows as SettlementRow[]);
}

/** Shared backend parameter builder for screen, previous-day mode, upcoming payments and Excel. */
export function settlementQuery(params: SettlementParams): string {
    // Plan dates are DateOnly on the server: preserve the organization calendar day.
    const dateParam = (value: string | Date, end: boolean) =>
        new Date(
            params.byPlanPaymentDate && typeof value === "string"
                ? `${value.slice(0, 10)}T${end ? "23:59:59.999" : "00:00:00.000"}Z`
                : value,
        );
    const statuses = Array.isArray(params.paymentStatus)
        ? params.paymentStatus
        : params.paymentStatus
          ? [params.paymentStatus]
          : [];
    const filter = {
        ...(params.startDate && params.endDate
            ? {
                  StartedAt: { gt: dateParam(params.startDate, false) },
                  EndedAt: { lt: dateParam(params.endDate, true) },
              }
            : {}),
        ...(params.counterpartyId ? { CounterpartyId: params.counterpartyId } : {}),
        ...(params.carId ? { CarId: params.carId } : {}),
        ...(statuses.length ? { PaymentStatuses: { any: { [ITEM_ROOT]: { in: statuses } } } } : {}),
        GroupByDates: !!params.groupByDates,
        GroupByCounterparties: !params.groupByDates && !!params.groupByCounterparties,
        OnlyOverdue: !!params.onlyOverdue,
        PreviousDayPaymentsOnly: !!params.previousDayPaymentsOnly,
        ByPlanPaymentDate: !!params.byPlanPaymentDate,
        ...(!params.isAccountsPayable && params.source !== undefined ? { Source: params.source } : {}),
    };
    return `paramsQuery=${buildQuery({ filter }).slice(1)}`;
}

/** Session storage is scoped to user, organization and demo mode. */
export function settlementStorageKey(userId: number, organizationId: number, demo: boolean): string {
    return `mutualSettlements:${userId}:${organizationId}:${demo ? "demo" : "company"}`;
}

/** Ignore expired/malformed stored state instead of preventing the report from opening. */
export function readSettlementState(storage: Pick<Storage, "getItem">, key: string): Record<string, unknown> {
    try {
        const value = JSON.parse(storage.getItem(key) ?? "null");
        if (!value || typeof value !== "object" || value.version !== 1) {
            return {};
        }
        const state = value.state;
        if (!state || typeof state !== "object" || !Array.isArray(state.settlementTableState?.filters ?? [])) {
            return {};
        }
        return state;
    } catch {
        return {};
    }
}

/** Persist only report settings, never fetched data or in-progress payment edits. */
export function writeSettlementState(
    storage: Pick<Storage, "setItem">,
    key: string,
    state: Record<string, unknown>,
): void {
    const names = [
        "startDate",
        "endDate",
        "isAccountsPayable",
        "source",
        "groupByDates",
        "groupByCounterparties",
        "onlyOverdue",
        "previousDayPaymentsOnly",
        "settlementTableState",
    ];
    try {
        storage.setItem(
            key,
            JSON.stringify({ version: 1, state: Object.fromEntries(names.map((name) => [name, state[name]])) }),
        );
    } catch {
        // Restricted or full storage must not block work with the report.
    }
}
